<template>
  <div class="view-container">
    <div class="stats-row">
      <div class="stat-card" v-for="s in stats" :key="s.label" :style="{ borderTopColor: s.color }">
        <div class="stat-icon" :style="{ background: s.color }"><i :class="s.icon"></i></div>
        <div class="stat-info">
          <div class="stat-value">{{ s.value }}</div>
          <div class="stat-label">{{ s.label }}</div>
        </div>
      </div>
    </div>

    <div class="content-grid">
      <div class="grid-left">
        <div class="card">
          <div class="card-header"><h3><i class="fas fa-clock"></i> 最近更新文档</h3></div>
          <div class="card-body">
            <table class="simple-table">
              <thead><tr><th>文档名称</th><th>版本</th><th>更新人</th><th>更新时间</th></tr></thead>
              <tbody>
                <tr v-for="d in recentDocs" :key="d.id" @click="onViewDoc(d)" style="cursor:pointer">
                  <td><i class="fas" :class="getFileIcon(d.type)"></i> {{ d.name }}</td>
                  <td><span class="version-tag">v{{ d.version }}</span></td>
                  <td>{{ d.author }}</td>
                  <td class="time-cell">{{ d.updateTime }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="card">
          <div class="card-header"><h3><i class="fas fa-hourglass-half"></i> 待审批文档</h3></div>
          <div class="card-body">
            <table class="simple-table">
              <thead><tr><th>文档名称</th><th>提交人</th><th>提交时间</th><th>操作</th></tr></thead>
              <tbody>
                <tr v-for="d in pendingDocs" :key="d.id">
                  <td><i class="fas" :class="getFileIcon(d.type)"></i> {{ d.name }}</td>
                  <td>{{ d.submitter }}</td>
                  <td class="time-cell">{{ d.submitTime }}</td>
                  <td><button class="btn btn-primary btn-xs" @click="onAudit(d)">审批</button></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div class="grid-right">
        <div class="card">
          <div class="card-header"><h3><i class="fas fa-chart-pie"></i> 文档分类统计</h3></div>
          <div class="card-body">
            <VChart class="chart" :option="categoryChart" autoresize />
          </div>
        </div>
        <div class="card">
          <div class="card-header"><h3><i class="fas fa-chart-line"></i> 月度文档变更趋势</h3></div>
          <div class="card-body">
            <VChart class="chart" :option="trendChart" autoresize />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { PieChart, BarChart, LineChart } from 'echarts/charts'
import { TitleComponent, TooltipComponent, LegendComponent, GridComponent } from 'echarts/components'
import VChart from 'vue-echarts'
import { getDocumentStats, getRecentDocuments, getPendingDocuments } from '../api/document'

use([CanvasRenderer, PieChart, BarChart, LineChart, TitleComponent, TooltipComponent, LegendComponent, GridComponent])

const stats = ref([
  { label: '文档总数', value: 0, icon: 'fas fa-file-alt', color: '#3b82f6' },
  { label: '本月新增', value: 0, icon: 'fas fa-plus-circle', color: '#10b981' },
  { label: '待审批', value: 0, icon: 'fas fa-hourglass-half', color: '#f59e0b' },
  { label: '我的文档', value: 0, icon: 'fas fa-user-edit', color: '#8b5cf6' }
])

const recentDocs = ref([])
const pendingDocs = ref([])
const loading = ref(false)

const categoryChart = ref({
  tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
  series: [{
    type: 'pie',
    radius: ['50%', '75%'],
    center: ['50%', '50%'],
    data: [
      { value: 620, name: '质量手册', itemStyle: { color: '#3b82f6' } },
      { value: 480, name: '程序文件', itemStyle: { color: '#10b981' } },
      { value: 720, name: '作业指导书', itemStyle: { color: '#f59e0b' } },
      { value: 380, name: '记录表格', itemStyle: { color: '#8b5cf6' } },
      { value: 210, name: '外来文件', itemStyle: { color: '#ef4444' } },
      { value: 158, name: '其他', itemStyle: { color: '#6b7280' } }
    ],
    label: { fontSize: 10, formatter: '{b}\n{d}%' }
  }]
})

const trendChart = ref({
  tooltip: { trigger: 'axis' },
  grid: { left: 50, right: 20, top: 20, bottom: 30 },
  xAxis: { type: 'category', data: ['1月', '2月', '3月', '4月', '5月', '6月'], axisLabel: { fontSize: 10 } },
  yAxis: { type: 'value', axisLabel: { fontSize: 10 } },
  series: [
    { name: '新增', type: 'bar', data: [38, 42, 55, 48, 62, 47], itemStyle: { color: '#3b82f6', borderRadius: [3, 3, 0, 0] } },
    { name: '修订', type: 'bar', data: [12, 18, 22, 15, 28, 20], itemStyle: { color: '#f59e0b', borderRadius: [3, 3, 0, 0] } },
    { name: '废止', type: 'line', data: [5, 3, 8, 4, 6, 2], smooth: true, itemStyle: { color: '#ef4444' } }
  ]
})

const getFileIcon = (type) => {
  const icons = { 
    doc: 'fa-file-word', 
    docx: 'fa-file-word',
    xls: 'fa-file-excel',
    xlsx: 'fa-file-excel', 
    pdf: 'fa-file-pdf', 
    ppt: 'fa-file-powerpoint',
    pptx: 'fa-file-powerpoint', 
    img: 'fa-file-image',
    jpg: 'fa-file-image',
    png: 'fa-file-image'
  }
  return icons[type] || 'fa-file'
}

const onViewDoc = (doc) => {
  window.$message.info('查看文档: ' + doc.name)
}

const onAudit = (doc) => {
  window.$message.info('审批: ' + doc.name)
}

/**
 * 加载统计数据
 */
const loadStats = async () => {
  try {
    loading.value = true
    const response = await getDocumentStats()
    const data = response.data || {}
    stats.value[0].value = data.totalCount || 0
    stats.value[1].value = data.monthlyNew || 0
    stats.value[2].value = data.pendingCount || 0
    stats.value[3].value = data.myDocCount || 0
    
    // 更新图表数据
    if (data.categoryStats) {
      categoryChart.value.series[0].data = data.categoryStats
    }
    if (data.trendData) {
      trendChart.value.xAxis.data = data.trendData.months || []
      trendChart.value.series[0].data = data.trendData.newDocs || []
      trendChart.value.series[1].data = data.trendData.modifiedDocs || []
      trendChart.value.series[2].data = data.trendData.obsoleteDocs || []
    }
  } catch (error) {
    console.error('加载统计数据失败:', error)
  } finally {
    loading.value = false
  }
}

/**
 * 加载最近文档
 */
const loadRecentDocs = async () => {
  try {
    const response = await getRecentDocuments(5)
    recentDocs.value = (response.data || []).map(doc => ({
      id: doc.guid,
      name: doc.titles,
      type: doc.contentType,
      version: doc.vesion || '1.0',
      author: doc.ownerDspName || doc.ownerAccount,
      updateTime: formatDateTime(doc.updateTime || doc.createTime)
    }))
  } catch (error) {
    console.error('加载最近文档失败:', error)
  }
}

/**
 * 加载待审批文档
 */
const loadPendingDocs = async () => {
  try {
    const response = await getPendingDocuments()
    pendingDocs.value = (response.data || []).map(doc => ({
      id: doc.guid,
      name: doc.titles,
      type: doc.contentType,
      submitter: doc.submitterName || doc.submitter,
      submitTime: formatDateTime(doc.submitTime)
    }))
  } catch (error) {
    console.error('加载待审批文档失败:', error)
  }
}

/**
 * 格式化日期时间
 */
const formatDateTime = (datetime) => {
  if (!datetime) return ''
  try {
    const d = new Date(datetime)
    if (isNaN(d.getTime())) return ''
    return d.toISOString().slice(0, 16).replace('T', ' ')
  } catch (e) {
    console.warn('日期格式化失败:', datetime, e)
    return ''
  }
}

// 组件挂载时加载数据
onMounted(() => {
  loadStats()
  loadRecentDocs()
  loadPendingDocs()
})
</script>

<style scoped>
.view-container { padding: 20px; background: #f5f7fa; overflow-y: auto; max-height: calc(100vh - 130px); }
.stats-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 20px; }
.stat-card { background: #fff; border-radius: 8px; border: 1px solid #e8ecf1; border-top: 3px solid #3b82f6; padding: 20px; display: flex; align-items: center; gap: 16px; }
.stat-icon { width: 48px; height: 48px; border-radius: 10px; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 20px; }
.stat-value { font-size: 28px; font-weight: 700; color: #1f2937; }
.stat-label { font-size: 13px; color: #6b7280; margin-top: 2px; }
.content-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
.grid-left, .grid-right { display: flex; flex-direction: column; gap: 20px; }
.card { background: #fff; border-radius: 8px; border: 1px solid #e8ecf1; overflow: hidden; }
.card-header { padding: 12px 16px; border-bottom: 1px solid #e8ecf1; background: #fafbfc; }
.card-header h3 { margin: 0; font-size: 14px; font-weight: 600; color: #374151; display: flex; align-items: center; gap: 8px; }
.card-body { padding: 12px; }
.simple-table { width: 100%; border-collapse: collapse; }
.simple-table th, .simple-table td { padding: 8px 10px; border-bottom: 1px solid #f3f4f6; font-size: 12px; color: #374151; text-align: left; }
.simple-table th { font-weight: 600; color: #6b7280; background: #f8fafc; }
.simple-table tr:hover { background: #f9fafb; }
.time-cell { color: #9ca3af; font-size: 11px; }
.version-tag { background: #ede9fe; color: #7c3aed; padding: 1px 6px; border-radius: 3px; font-size: 11px; }
.chart { height: 280px; }
.btn { padding: 4px 10px; border: 1px solid transparent; border-radius: 4px; font-size: 12px; cursor: pointer; display: inline-flex; align-items: center; gap: 4px; }
.btn-primary { background: #3b82f6; color: #fff; border-color: #3b82f6; }
.btn-primary:hover { background: #2563eb; }
.btn-xs { padding: 3px 8px; font-size: 11px; }
</style>
